"use client";

import { c as _c } from "react/compiler-runtime";
import { useHotkey } from "@tanstack/react-hotkeys";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { chatOpenAtom } from "@/lib/atoms/chat-atoms";
import { usePreferencesHydrated, usePreferencesStore } from "@/lib/stores/preferences-store";
import { CHAT_HOTKEY, ChatFab } from "./chat-fab";
interface LoadedChatClientProps {
  suggestions?: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReady: () => void;
}
type ChatClientModule = {
  ChatClient: React.ComponentType<LoadedChatClientProps>;
};
type ChatClientLoader = () => Promise<ChatClientModule>;
const loadChatClient: ChatClientLoader = () => import("./chat-client");
export function ChatClientLazy(t0) {
  const $ = _c(41);
  const {
    suggestions,
    loader: t1
  } = t0;
  const loader = t1 === undefined ? loadChatClient : t1;
  const hydrated = usePreferencesHydrated();
  const hideAiFeatures = usePreferencesStore(_temp);
  const setHideAiFeatures = usePreferencesStore(_temp2);
  const [open, setOpen] = useAtom(chatOpenAtom);
  const [ChatClient, setChatClient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const loadingRef = useRef(false);
  const loadPromiseRef = useRef(null);
  let t2;
  if ($[0] !== loader) {
    t2 = () => {
      if (loadPromiseRef.current) {
        return loadPromiseRef.current;
      }
      const promise = loader().catch(error => {
        loadPromiseRef.current = null;
        throw error;
      });
      loadPromiseRef.current = promise;
      return promise;
    };
    $[0] = loader;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const load = t2;
  let t3;
  if ($[2] !== ChatClient || $[3] !== load) {
    t3 = () => {
      if (!ChatClient) {
        load().catch(_temp3);
      }
    };
    $[2] = ChatClient;
    $[3] = load;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handlePrefetch = t3;
  let t4;
  let t5;
  if ($[5] !== hideAiFeatures || $[6] !== hydrated || $[7] !== setHideAiFeatures) {
    t4 = () => {
      if (!hydrated) {
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("show_ai") === "1" && hideAiFeatures) {
        setHideAiFeatures(false);
        urlParams.delete("show_ai");
        const newUrl = urlParams.toString() === "" ? window.location.pathname : `${window.location.pathname}?${urlParams.toString()}`;
        window.history.replaceState({}, "", newUrl);
      }
    };
    t5 = [hydrated, hideAiFeatures, setHideAiFeatures];
    $[5] = hideAiFeatures;
    $[6] = hydrated;
    $[7] = setHideAiFeatures;
    $[8] = t4;
    $[9] = t5;
  } else {
    t4 = $[8];
    t5 = $[9];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[10] !== hideAiFeatures || $[11] !== setOpen) {
    t6 = () => {
      if (hideAiFeatures) {
        setOpen(false);
      }
    };
    t7 = [hideAiFeatures, setOpen];
    $[10] = hideAiFeatures;
    $[11] = setOpen;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] !== ChatClient || $[15] !== load || $[16] !== setOpen) {
    t8 = () => {
      if (ChatClient) {
        setOpen(true);
        return;
      }
      if (loadingRef.current) {
        return;
      }
      loadingRef.current = true;
      setLoading(true);
      setLoadError(false);
      setOpen(true);
      load().then(module => {
        setChatClient(() => module.ChatClient);
      }).catch(() => {
        loadingRef.current = false;
        setLoading(false);
        setLoadError(true);
        setOpen(false);
      });
    };
    $[14] = ChatClient;
    $[15] = load;
    $[16] = setOpen;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const handleOpen = t8;
  let t9;
  if ($[18] !== ChatClient || $[19] !== handleOpen || $[20] !== setOpen) {
    t9 = () => {
      if (ChatClient) {
        setOpen(_temp4);
        return;
      }
      handleOpen();
    };
    $[18] = ChatClient;
    $[19] = handleOpen;
    $[20] = setOpen;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const handleActivation = t9;
  const t10 = hydrated && !hideAiFeatures;
  let t11;
  if ($[22] !== t10) {
    t11 = {
      conflictBehavior: "allow",
      enabled: t10
    };
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  useHotkey(CHAT_HOTKEY, handleActivation, t11);
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      loadingRef.current = false;
      setLoading(false);
    };
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const handleReady = t12;
  if (!hydrated) {
    return null;
  }
  let t13;
  if ($[25] !== handleActivation || $[26] !== handlePrefetch || $[27] !== hideAiFeatures || $[28] !== loading) {
    t13 = !hideAiFeatures && <ChatFab loading={loading} onClick={handleActivation} onPrefetch={handlePrefetch} />;
    $[25] = handleActivation;
    $[26] = handlePrefetch;
    $[27] = hideAiFeatures;
    $[28] = loading;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = loadError ? "Chat failed to load. Try again." : "";
  let t15;
  if ($[30] !== t14) {
    t15 = <p className="sr-only" aria-live="polite">{t14}</p>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== ChatClient || $[33] !== open || $[34] !== setOpen || $[35] !== suggestions) {
    t16 = ChatClient && <ChatClient suggestions={suggestions} open={open} onOpenChange={setOpen} onReady={handleReady} />;
    $[32] = ChatClient;
    $[33] = open;
    $[34] = setOpen;
    $[35] = suggestions;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t13 || $[38] !== t15 || $[39] !== t16) {
    t17 = <>{t13}{t15}{t16}</>;
    $[37] = t13;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
}
function _temp4(current) {
  return !current;
}
function _temp3() {}
function _temp2(s_0) {
  return s_0.setHideAiFeatures;
}
function _temp(s) {
  return s.hideAiFeatures;
}